import React, { useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Amplify } from 'aws-amplify';
import { datadogRum } from '@datadog/browser-rum';
import {
  QueryClientProvider,
  QueryCache,
  QueryClient,
  MutationCache,
} from 'react-query';
import toast from 'react-hot-toast';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Wrapper as GoogleMapsWrapper } from '@googlemaps/react-wrapper';
import { Toaster } from 'react-hot-toast';

import { amplify_config } from './amplify-config';
import { setUserDetails } from './store/auth/actions';
import {
  parseJwt,
  cookieToken,
  hasCookieToken,
  isLifelineMode,
  isSmartPlugMode,
  isAdminDashboard,
  isPlayGroundMode,
} from './utils/CookieToken';
import { setJumboHeaders } from './analytics/jumboClient';
import Routes from './routes';
import DatadogViewTracker from './analytics/datadogViewTracker';

export const isUserLogged = 'isUserLogged';

Amplify.configure({
  Auth: amplify_config,
});

// react query configs
const mutationCache = new MutationCache({
  onSuccess(variable, data, context, mutation) {
    if (mutation?.meta?.hideAlert) {
      return;
    }
    if (variable?.isError) {
      return toast.error(variable?.message || 'Something went wrong');
    } else if (!mutation?.meta?.doNotShowSuccessToast) toast.success('success');
  },
  onError(error, variables, context, mutation) {
    if (mutation?.meta?.hideErrorAlert) return;
    if (error?.error?.reason) {
      return toast.error(error.error.reason);
    }
    return error?.message && toast.error(error.message);
  },
});

const queryCache = new QueryCache({
  onError(error, query) {
    if (query?.meta?.hideErrorAlert) return;
    if (error?.error?.reason) return toast.error(error.error.reason);

    return error?.message && toast.error(error.message);
  },
});

const queryClient = new QueryClient({
  mutationCache,
  queryCache,
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry() {
        return false;
      },
    },
  },
});

const ToasterComponent = (
  <Toaster
    position='top-center'
    toastOptions={{
      duration: 5000,
      style: {
        maxWidth: '70%',
        fontWeight: 500,
      },
      error: {
        style: {
          background: '#FFEDEF',
          color: '#EF4F5F',
        },
      },
      success: {
        style: {
          background: '#EBFFEF',
          color: '#318616',
        },
        iconTheme: {
          primary: '#318616',
        },
      },
    }}
  />
);

const App = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    setJumboHeaders();
    if (!hasCookieToken) {
      sessionStorage.setItem(isUserLogged, false);
      return;
    }

    const decodedToken = parseJwt(cookieToken);

    let userDetails = {
      external_user_id: decodedToken?.external_user_id,
      crm_user_id: decodedToken?.id,
      name: decodedToken?.name,
      email_id: decodedToken?.email_id,
    };

    try {
      sessionStorage.setItem(isUserLogged, true);
      dispatch(setUserDetails({ ...userDetails }));
      if (userDetails.external_user_id) {
        datadogRum.setUser({
          id: String(userDetails.external_user_id),
          crm_user_id: String(userDetails.crm_user_id),
        });
      }

      if (datadogRum.getInitConfiguration()) {
        const dashboardMode = isSmartPlugMode
          ? 'smartplug'
          : isAdminDashboard
          ? 'admin'
          : isLifelineMode
          ? 'lifeline'
          : 'unknown';

        datadogRum.setGlobalContextProperty('dashboard_mode', dashboardMode);
        datadogRum.setGlobalContextProperty(
          'org_id',
          sessionStorage.getItem('org_id') || '1',
        );
        datadogRum.setGlobalContextProperty('is_playground', isPlayGroundMode);
      }
    } catch (err) {
      console.log('err', err);
    }
  }, [hasCookieToken]);

  return (
    <Router>
      <DatadogViewTracker />
      <QueryClientProvider client={queryClient}>
        <GoogleMapsWrapper
          libraries={['geometry', 'places']}
          apiKey={window.googleMapsApiKey}
        >
          <div className='App'>
            <Routes />
          </div>
        </GoogleMapsWrapper>

        {ToasterComponent}

        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Router>
  );
};

export default App;
